import PropTypes from "prop-types";

import { alignItems } from "../../consts";

import { FlexContainers, Heading4, IconButton, SVG, WrapperContainer } from "../../components";
import {
  Footer,
  Header,
  ModalBackground,
  ModalContainer,
  ModalContent,
  ModalContentContainer,
  RightSideModalBackground,
  RightSideModalContainer
} from "./styled";
import { useEffect, useRef, useState } from "react";

const Modal = ({
  id,
  active,
  setActive = () => {},
  title,
  closable = true,
  fillAvailable,
  children,
  Container,
  isRightSideModal,
  preventOverflow,
  preventContentScroll,
  hideContentOverflow,
  isFullHeight,
  alignRight,
  backgroundColor,
  modalHeader,
  modalFooter,
  closeOnBackgroundClick,
  closingDisabled,
  showFooterBorder,
  withContentVerticalBorders
}) => {
  const modalContentRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const content = modalContentRef?.current;
    if (!content) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setIsOverflow(content.offsetHeight < content.scrollHeight);
    });

    observer.observe(content);

    return () => {
      observer.disconnect();
    };
  }, [modalContentRef?.current]);

  const modalContent = (
    <WrapperContainer
      Container={FlexContainers.FlexColumnBetween}
      height="100%"
      width="100%"
      onClick={(event) => event.stopPropagation()}>
      {(modalHeader || title || closable) && (
        <Header>
          {modalHeader && modalHeader}
          {title && <Heading4>{title}</Heading4>}
          {closable && (
            <IconButton
              onClick={() => setActive(false)}
              id="close-button"
              disabled={closingDisabled}>
              <SVG.CloseIcon />
            </IconButton>
          )}
        </Header>
      )}

      <ModalContent
        ref={modalContentRef}
        preventOverflow={preventOverflow}
        fillAvailable={fillAvailable}
        hideContentOverflow={hideContentOverflow}
        isOverflow={isOverflow}
        showFooterBorder={showFooterBorder}
        hasHeader={!!modalHeader || closable || title}
        hasFooter={!!modalFooter}
        withContentVerticalBorders={withContentVerticalBorders}>
        <ModalContentContainer alignRight={alignRight}>{children}</ModalContentContainer>
      </ModalContent>
      {modalFooter && <Footer>{modalFooter}</Footer>}
    </WrapperContainer>
  );

  const ModalBackgroundContainer = isRightSideModal ? RightSideModalBackground : ModalBackground;
  const ModalContentWrapper = isRightSideModal ? RightSideModalContainer : ModalContainer;

  return (
    <ModalBackgroundContainer
      data-testid={id}
      className={active && "active"}
      isFullHeight={isFullHeight}
      alignRight={alignRight}
      onClick={() => closeOnBackgroundClick && !closingDisabled && setActive(false)}>
      {Container ? (
        <Container alignItems={alignItems.start} className={active && "active"} position="relative">
          {modalContent}
        </Container>
      ) : (
        <ModalContentWrapper
          alignItems={alignItems.start}
          className={active && "active"}
          position="relative"
          backgroundColor={backgroundColor}
          preventContentScroll={preventContentScroll}>
          {modalContent}
        </ModalContentWrapper>
      )}
    </ModalBackgroundContainer>
  );
};

Modal.propTypes = {
  id: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  setActive: PropTypes.func,
  title: PropTypes.string,
  closable: PropTypes.bool,
  fillAvailable: PropTypes.bool,
  children: PropTypes.node.isRequired,
  Container: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
  isRightSideModal: PropTypes.bool,
  preventOverflow: PropTypes.bool,
  preventContentScroll: PropTypes.bool,
  hideContentOverflow: PropTypes.bool,
  isFullHeight: PropTypes.bool,
  alignRight: PropTypes.bool,
  backgroundColor: PropTypes.string,
  modalHeader: PropTypes.node,
  modalFooter: PropTypes.node,
  closeOnBackgroundClick: PropTypes.bool,
  closingDisabled: PropTypes.bool,
  showFooterBorder: PropTypes.bool,
  withContentVerticalBorders: PropTypes.bool
};

export default Modal;
